import { useRef, useState } from "react";

import {
  faBracketsCurly,
  faChartSimple,
  faDatabase,
  faFlagAlt,
  faMagnifyingGlass,
  faSliders,
  faSparkles,
  faWavePulse,
} from "@fortawesome/pro-regular-svg-icons";
import { useReactFlow } from "@xyflow/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEventListener } from "usehooks-ts";
import { validate as isValidUUID } from "uuid";

import { useHistoryDecisionV3Basic } from "src/api/decisionHistoryV2/decisionHistoryQueries";
import { useIsFloatingWindowOpen } from "src/base-components/FloatingWindow/hooks";
import { Icon } from "src/base-components/Icon";
import {
  DatabaseConnectionNodeData,
  IntegrationNodeData,
  ManifestConnectionNodeData,
} from "src/constants/NodeDataTypes";
import { GroupIcon, getNodeIconFromNode } from "src/constants/NodeIcons";
import {
  NODE_TYPE,
  STRUCTURAL_NODE_TYPES,
  StructuralNodeType,
} from "src/constants/NodeTypes";
import { useDatasets } from "src/datasets/api/queries";
import { useDecisionsOverview } from "src/decisionsOverview/queries";
import { invalidEntityIdsPattern } from "src/decisionsOverview/utils";
import { ExcludesFalse } from "src/flow/types";
import { useFlowGraphSearchWindow } from "src/flowGraph/useFlowGraphSearchWindow";
import { useIsTaktileOrgUser } from "src/hooks/useIsTaktileOrgUser";
import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";
import { useJobsByRunId } from "src/jobs/api/queries";
import { ItemIcon } from "src/omnibox/ItemIcon";
import { MenuItem, OmniboxBase } from "src/omnibox/OmniboxBase";
import { OmniboxFeatureFlagLabel } from "src/omnibox/OmniboxFeatureFlagLabel";
import {
  useIsOmniboxVisible,
  useOmniboxActions,
} from "src/omnibox/OmniboxStore";
import { FEATURE_FLAGS } from "src/router/featureFlags";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { LeftPaneOptions } from "src/router/SearchParams";
import {
  DashboardPageParams,
  getUrlToDecisionsOverview,
  getUrlToJobPage,
} from "src/router/urls";
import { useGraphStore } from "src/store/StoreProvider";
import { getParentGroup } from "src/utils/GraphUtils";
import { logger } from "src/utils/logger";
import { useParamsDecode } from "src/utils/useParamsDecode";
import { uuidRegex } from "src/utils/uuidRegex";

export const OmniboxAuthoring = () => {
  const [_, setSearchParams] = useSearchParams();
  const [userInput, setUserInput] = useState<string>("");

  const { workspace, flow } = useAuthoringContext();
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const isOmniboxVisible = useIsOmniboxVisible();
  const { showOmnibox, hideOmnibox } = useOmniboxActions();
  const isTaktileUser = useIsTaktileOrgUser();
  const { entitiesEnabled: entitiesFeatureGateEnabled } =
    useWorkspaceFeatureGates();
  const document = useRef(window.document);
  const navigate = useNavigate();
  const { fitView, getNode } = useReactFlow();
  const graphStore = useGraphStore();
  const { setSelectedNode, toggleGroupNode } = useGraphStore();
  const { openSearchWindow } = useFlowGraphSearchWindow();
  const isSearchWindowOpen = useIsFloatingWindowOpen("flow-graph-search");

  // Entity query for searching by entity_id
  const decisionSummaryQuery = useDecisionsOverview(
    workspace.base_url,
    {
      entity_id: userInput,
      groupby: "flow.id",
      aggregate: "count",
    },
    {
      enabled: Boolean(
        userInput && !entitiesFeatureGateEnabled && isOmniboxVisible,
      ),
    },
  );

  // Decision query for searching by decision_id
  const decisionQuery = useHistoryDecisionV3Basic(
    {
      decisionId: userInput,
      baseUrl: workspace.base_url!,
    },
    {
      enabled: isValidUUID(userInput) && isOmniboxVisible,
    },
  );

  // Job run query for searching by run_id
  const jobsByRunIdQuery = useJobsByRunId(
    {
      baseURL: workspace.base_url ?? "",
      runId: userInput,
    },
    {
      enabled: isValidUUID(userInput) && isOmniboxVisible,
    },
  );

  const searchMenuItemsVisibility = {
    isDecision:
      decisionQuery.isFetching ||
      (decisionQuery.isSuccess && !!decisionQuery.data),
    isEntity:
      decisionSummaryQuery.isFetching ||
      (decisionSummaryQuery.isSuccess &&
        decisionSummaryQuery.data.groups.length > 0),
    isJobRun:
      jobsByRunIdQuery.isFetching ||
      (jobsByRunIdQuery.isSuccess && jobsByRunIdQuery.data.jobs.length > 0),
  };

  const handleClose = () => {
    hideOmnibox();
    setUserInput("");
  };

  const handleKeydown = (e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.code === "KeyK" && isTaktileUser) {
      showOmnibox();
    }
    if ((e.metaKey || e.ctrlKey) && e.code === "KeyF" && !isSearchWindowOpen) {
      e.preventDefault();
      openSearchWindow();
    }
    if (isOmniboxVisible && e.key === "Escape") {
      handleClose();
    }
  };

  useEventListener("keydown", handleKeydown, document);

  // Fetch the relevant data for the omnibox items: list of nodes, node-groups, feature flags, datasets, and left-pane actions
  const nodes = graphStore.nodesArray;
  const groupNodes = graphStore.groupsArray;
  const datasetsQuery = useDatasets({
    flowId: flow.id,
    baseUrl: workspace.base_url,
  });
  const datasets = datasetsQuery.data || [];

  const leftPaneOptions: {
    key: LeftPaneOptions;
    text: string;
    icon: React.ReactNode;
  }[] = [
    {
      key: LeftPaneOptions.Datasets,
      text: "Go to Test data",
      icon: <Icon color="text-gray-500" icon={faDatabase} size="xs" />,
    },
    {
      key: LeftPaneOptions.Schema,
      text: "Go to Schema",
      icon: <Icon color="text-gray-500" icon={faBracketsCurly} size="xs" />,
    },
    {
      key: LeftPaneOptions.Parameters,
      text: "Go to Parameters",
      icon: <Icon color="text-gray-500" icon={faSliders} size="xs" />,
    },
    {
      key: LeftPaneOptions.DecisionHistory,
      text: "Go to Decision history",
      icon: <Icon color="text-gray-500" icon={faWavePulse} size="xs" />,
    },
    {
      key: LeftPaneOptions.Analytics,
      text: "Go to Analytics",
      icon: <Icon color="text-gray-500" icon={faChartSimple} size="xs" />,
    },
  ];

  // Create omnibox items list for nodes, node-groups, feature flags, datasets, and left-pane actions
  const nodeItems: MenuItem[] = nodes.reduce(
    (nodeMenuItems: MenuItem[], node) => {
      const nodeType = node.type as NODE_TYPE;

      if (STRUCTURAL_NODE_TYPES.includes(nodeType as StructuralNodeType)) {
        return nodeMenuItems;
      }

      const icon = getNodeIconFromNode(node, "sm");

      // Extract provider for relevant node types
      let provider: string | undefined;
      if (
        [
          NODE_TYPE.MANIFEST_CONNECTION_NODE,
          NODE_TYPE.SQL_DATABASE_CONNECTION_NODE,
          NODE_TYPE.INTEGRATION_NODE,
        ].includes(nodeType)
      ) {
        const nodeData = node.data as
          | ManifestConnectionNodeData
          | DatabaseConnectionNodeData
          | IntegrationNodeData;
        provider = nodeData.providerResource?.provider;
      }

      nodeMenuItems.push({
        icon,
        label: node.data.label,
        details: node.type,
        provider,
        value: node.id,
        onSelect: () => selectItem(node.id, "Node"),
        type: "Node",
      });

      return nodeMenuItems;
    },
    [],
  );
  const datasetItems: MenuItem[] = datasets.map((dataset) => ({
    label: dataset.name,
    icon: (
      <div className="flex h-6 w-6 items-center justify-center rounded-md bg-gray-200 bg-opacity-50">
        <Icon color="text-gray-500" icon={faDatabase} size="xs" />
      </div>
    ),
    type: "Dataset",
    details: "",
    value: `edit-dataset-id=${dataset.id}`,
    onSelect: () =>
      setSearchParams({
        "edit-dataset-id": dataset.id,
      }),
  }));
  const groupNodeItems: MenuItem[] = groupNodes.map((group) => ({
    label: group.data.label,
    icon: (
      <div className="mt-0.5 ml-0.5 pt-px pl-0.5">
        <GroupIcon groupId={group.id} size="sm" />
      </div>
    ),
    type: "Node Groups",
    details: "",
    value: group.id,
    onSelect: () => selectItem(group.id, "Node Groups"),
  }));
  const leftPaneItems = leftPaneOptions.map(({ key, icon, text }) => ({
    label: text,
    icon: (
      <div className="h-6 w-6 items-center justify-center rounded-md bg-gray-200 bg-opacity-50 pt-0.5 pl-px">
        {icon}
      </div>
    ),
    type: "Action",
    details: "",
    value: key,
    onSelect: () => selectItem(key, "Action"),
  }));
  const featureFlagItems: MenuItem[] = Object.entries(FEATURE_FLAGS).map(
    ([key, value]) => ({
      label: <OmniboxFeatureFlagLabel featureFlag={value} />,
      icon: (
        <div className="flex h-6 w-6 items-center justify-center rounded-md bg-gray-200 bg-opacity-50">
          <Icon color="text-gray-500" icon={faFlagAlt} size="xs" />
        </div>
      ),
      type: "Feature Flag",
      details: value,
      value: key,
      onSelect: () => selectItem(value, "Feature Flag"),
    }),
  );
  const actionItems: MenuItem[] = [
    searchMenuItemsVisibility.isEntity && {
      label: "Search for an Entity ID",
      icon: (
        <ItemIcon
          bgColor="bg-gray-200"
          color="text-gray-500"
          icon={faSparkles}
          isLoading={decisionSummaryQuery.isFetching}
        />
      ),
      type: "Action",
      subType: "Entity ID",
      details: "",
      value: "entity",
      onSelect: (query: string) => selectItem("entity", "Action", query),
      dataLoc: "omnibox-entity-search",
      pattern: invalidEntityIdsPattern,
      disabled: decisionSummaryQuery.isFetching,
    },
    searchMenuItemsVisibility.isDecision && {
      label: "Search for a Decision ID",
      icon: (
        <ItemIcon
          bgColor="bg-gray-200"
          color="text-gray-500"
          icon={faSparkles}
          isLoading={decisionQuery.isFetching}
        />
      ),
      type: "Action",
      subType: "Decision ID",
      details: "",
      value: "decision",
      onSelect: (query: string) => selectItem("decision", "Action", query),
      dataLoc: "omnibox-decision-search",
      pattern: uuidRegex,
      disabled: decisionQuery.isFetching,
    },
    searchMenuItemsVisibility.isJobRun && {
      label: "Search for a Job run ID",
      icon: (
        <ItemIcon
          bgColor="bg-gray-200"
          color="text-gray-500"
          icon={faSparkles}
          isLoading={jobsByRunIdQuery.isFetching}
        />
      ),
      type: "Action",
      subType: "Job run ID",
      details: "",
      value: "jobrun",
      onSelect: (query: string) => selectItem("jobrun", "Action", query),
      dataLoc: "omnibox-jobrun-search",
      pattern: uuidRegex,
      disabled: jobsByRunIdQuery.isFetching,
    },
  ].filter(Boolean as unknown as ExcludesFalse);

  const selectItem = (
    value: string,
    type: "Node" | "Feature Flag" | "Node Groups" | "Action" | "Dataset",
    query: string = "",
  ) => {
    try {
      switch (type) {
        case "Node": {
          const nodeDetails = getNode(value);
          if (nodeDetails) {
            setSelectedNode(value);
            fitView({
              padding: 0.2,
              includeHiddenNodes: false,
              maxZoom: 1,
              minZoom: 0.4,
              duration: 300,
              nodes: [nodeDetails],
            });
          } else {
            const parentNode = getParentGroup(graphStore.groups, value);
            if (parentNode && !parentNode.data.uiState.expanded) {
              toggleGroupNode(parentNode.id, true);
              setTimeout(() => {
                const childNode = getNode(value);
                setSelectedNode(value);
                if (childNode) {
                  fitView({
                    padding: 0.2,
                    includeHiddenNodes: false,
                    maxZoom: 1,
                    minZoom: 0.4,
                    duration: 300,
                    nodes: [childNode],
                  });
                }
              }, 100);
            }
          }
          break;
        }

        case "Feature Flag":
          setSearchParams((searchParams) => {
            if (searchParams.has(value)) {
              searchParams.delete(value);
            } else {
              searchParams.append(value, "true");
            }
            return searchParams;
          });
          break;

        case "Node Groups": {
          toggleGroupNode(value, true);
          const groupNodeDetails = getNode(value);
          if (groupNodeDetails) {
            setSelectedNode(null);
            fitView({
              padding: 0.2,
              includeHiddenNodes: false,
              maxZoom: 1,
              minZoom: 0.4,
              duration: 300,
              nodes: [groupNodeDetails],
            });
          }
          break;
        }

        case "Dataset":
          setSearchParams({
            "edit-dataset-id": value.split("=")[1],
          });
          break;

        case "Action":
          if (value === "decision") {
            navigate(
              getUrlToDecisionsOverview(orgId, wsId, { decisionId: query }),
            );
          } else if (value === "entity") {
            navigate(
              getUrlToDecisionsOverview(orgId, wsId, { entityId: query }),
            );
          } else if (
            value === "jobrun" &&
            jobsByRunIdQuery.isSuccess &&
            jobsByRunIdQuery.data.jobs.length > 0
          ) {
            const job = jobsByRunIdQuery.data.jobs[0];
            navigate(getUrlToJobPage(orgId, wsId, job.flow_id, job.id));
          } else {
            setSearchParams({ "left-pane": value });
          }
          break;

        default:
          break;
      }
    } catch (error) {
      logger.error("Error selecting item:", error);
    }
  };

  const canvasSearchItem: MenuItem[] =
    userInput.length > 1
      ? [
          {
            label: "Search across user inputs in this Decision Flow version",
            hideSection: true,
            icon: (
              <ItemIcon
                bgColor="bg-gray-100"
                color="text-gray-500"
                icon={faMagnifyingGlass}
              />
            ),
            type: "Action",
            subType: "Action",
            details: "",
            value: "canvas-search",
            onSelect: () =>
              openSearchWindow({ searchTerm: userInput, isOmnibox: true }),
            dataLoc: "omnibox-canvas-search",
            alwaysInclude: true,
          },
        ]
      : [];

  const menuItems = isTaktileUser
    ? [
        ...canvasSearchItem,
        ...nodeItems,
        ...groupNodeItems,
        ...leftPaneItems,
        ...datasetItems,
        ...actionItems,
        ...featureFlagItems,
      ]
    : [
        ...canvasSearchItem,
        ...nodeItems,
        ...groupNodeItems,
        ...leftPaneItems,
        ...datasetItems,
        ...actionItems,
      ];

  return (
    <OmniboxBase
      data-loc="omnibox-canvas"
      menuItems={menuItems}
      open={isOmniboxVisible}
      placeholder={
        entitiesFeatureGateEnabled
          ? "Search for a Decision ID, Decision Flows, or actions"
          : "Search for a Decision ID, Entity ID, Decision Flows, or actions"
      }
      onClose={handleClose}
      onQuery={setUserInput}
    />
  );
};
